// src/App.jsx
// --- ETB WARTUNGSPROTOKOLL PWA – HAUPT-APP -----------------------------------
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import StammdatenPage from './pages/StammdatenPage';
import VnbVerwaltung from './pages/VnbVerwaltung';
import ProtokollUebersicht from './pages/ProtokollUebersicht';
import ProtokollFormPage from './pages/ProtokollFormPage';
import MonteurStart from './pages/MonteurStart';
import WartungPlanen from './pages/WartungPlanen';
import FaelligeWartungen from './pages/FaelligeWartungen';
import VertragsImport from './pages/VertragsImport';
import Kalender from './pages/Kalender';
import { LOGO_DATA_URL } from './lib/logo';
import { anzeigeName } from './lib/personen';
import { APP_VERSION } from './lib/version';
import { C, useIsMobile } from './pages/formui';
import { anzahl as wartendeAnzahl, beiAenderung as beiWarteschlangeAenderung, abarbeiten, starteAutomatik } from './lib/warteschlange';
import { starteFotoAutomatik } from './lib/fotoWarteschlange';
import { globaleFehlerFangen } from './lib/fehlerprotokoll';
import FehlerGrenze from './pages/FehlerGrenze';
import { t, useSprache, setzeSprache, SPRACHEN } from './lib/sprache';
import { SCHRIFT_STUFEN, schriftGroesse, setzeSchriftGroesse, beiSchriftAenderung } from './lib/anzeige';

// Sprachauswahl (Anna-Lena 14.09.2026: Kroatisch fuer die Monteure). Nur die
// Anzeige wechselt - gespeichert und gedruckt wird weiter deutsch.
function SprachWahl({ gross = false }) {
  const code = useSprache();
  return (
    <select value={code} onChange={e => setzeSprache(e.target.value)} aria-label="Sprache / Jezik"
      style={{ padding: gross ? '11px 12px' : '5px 6px', borderRadius: 7, border: '1px solid ' + C.gray200,
               background: C.white, color: C.navy, fontSize: gross ? 16 : 13, fontWeight: 700, fontFamily: 'inherit',
               width: gross ? '100%' : 'auto' }}>
      {SPRACHEN.map(s => <option key={s.code} value={s.code}>{gross ? s.name : s.code.toUpperCase()}</option>)}
    </select>
  );
}

// Schriftgroesse (Anna-Lena 04.10.2026). Drei Stufen, gespeichert auf dem Geraet.
// Keine Stufe unter 100 %: iOS zoomt sonst beim Antippen eines Feldes selbst
// hinein (siehe lib/anzeige.js).
function SchriftWahl() {
  const [wert, setWert] = useState(schriftGroesse());
  useEffect(() => beiSchriftAenderung(setWert), []);
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {SCHRIFT_STUFEN.map(s => {
        const aktiv = s.wert === wert;
        return (
          <button key={s.wert} type='button' onClick={() => setzeSchriftGroesse(s.wert)}
            aria-pressed={aktiv}
            style={{ flex: '1 1 110px', minHeight: 48, padding: '10px 14px', borderRadius: 9,
                     border: '1.5px solid ' + (aktiv ? C.navy : C.gray200),
                     background: aktiv ? C.navy : C.white, color: aktiv ? C.white : C.navy,
                     fontWeight: 800, fontSize: 15 * s.faktor, cursor: 'pointer', fontFamily: 'inherit' }}>
            {t(s.name)}
          </button>
        );
      })}
    </div>
  );
}

// Farben und die Mobil-Erkennung kommen aus pages/formui.js - dort stehen sie
// fuer die ganze App an einer Stelle. Vorher war die Palette viermal kopiert;
// ein Gruen zu aendern hiess, es an vier Stellen zu suchen.

// Logo kommt aus lib/logo.js -- fest eingebettet. Vorher wurde es von
// etb-karlsruhe.de geladen und fehlte im Trafohaeuschen ohne Empfang.

const ROLLE_TEXT = { admin: 'Verwaltung', buero: 'Büro', monteur: 'Monteur' };

// Ein Menuepunkt in der Kopfzeile. Bewusst wie ein Textlink, nicht wie ein
// Knopf: fuenf bunte Knoepfe nebeneinander sind unruhig.
function KopfLink({ onClick, children, leise = false }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        padding: '7px 11px', borderRadius: 7, border: 'none', cursor: 'pointer',
        background: hover ? C.gray100 : 'transparent',
        color: leise ? C.gray500 : C.gray700,
        fontSize: 14.5, fontWeight: 600,
      }}>
      {children}
    </button>
  );
}


// --- MOBILE-MENÜ (Hamburger) -------------------------------------------------
function MobileMenu({ isAdmin, zeigeProtokolle, onPlanen, onFaellig, onProtokolle, onKunden, onStationen, onVnb, onAdmin, onSignOut }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  const itemStyle = { display: 'block', width: '100%', textAlign: 'left', padding: '11px 14px', borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700 };
  return (
    <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button onClick={() => setOpen(o => !o)}
        aria-label="Menü"
        style={{ padding: '8px 14px', borderRadius: 7, background: C.gray100, border: '1px solid ' + C.gray200, color: C.navy, cursor: 'pointer', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
        ☰
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: C.white, border: '1px solid ' + C.gray200, borderRadius: 9, padding: 6, minWidth: 190, zIndex: 200, boxShadow: '0 8px 28px rgba(10,22,40,0.16)' }}>
          {zeigeProtokolle && onPlanen && (
            <button onClick={() => { onPlanen(); setOpen(false); }} style={{ ...itemStyle, color: C.navy }}>{t('Wartung planen')}</button>
          )}
          {zeigeProtokolle && onFaellig && (
            <button onClick={() => { onFaellig(); setOpen(false); }} style={{ ...itemStyle, color: C.gray700 }}>{t('Fällige Wartungen')}</button>
          )}
          {zeigeProtokolle && (
            <button onClick={() => { onProtokolle(); setOpen(false); }} style={{ ...itemStyle, color: C.gray700 }}>{t('Protokolle')}</button>
          )}
          <button onClick={() => { onKunden(); setOpen(false); }} style={{ ...itemStyle, color: C.gray700 }}>{t('Kunden')}</button>
          <button onClick={() => { onStationen(); setOpen(false); }} style={{ ...itemStyle, color: C.gray700 }}>{t('Stationen')}</button>
          {zeigeProtokolle && (
            <button onClick={() => { onVnb(); setOpen(false); }} style={{ ...itemStyle, color: C.gray700 }}>VNB</button>
          )}
          {isAdmin && (
            <button onClick={() => { onAdmin(); setOpen(false); }} style={{ ...itemStyle, color: C.gray700 }}>{t('Verwaltung')}</button>
          )}
          <button onClick={() => { onSignOut(); setOpen(false); }} style={{ ...itemStyle, color: C.gray500 }}>{t('Abmelden')}</button>
        </div>
      )}
    </div>
  );
}

// --- UNTERE NAVIGATIONSLEISTE ------------------------------------------------
// Vier Ziele, rollenabhaengig. Bewusst unten: der Daumen erreicht die obere
// rechte Ecke einhaendig nicht.
function UntereLeiste({ aktiv, istBuero, onWechsel }) {
  // Vier verschiedene Ziele. Beim Monteur fuehrten "Stationen" und "Suchen"
  // vorher beide an dieselbe Stelle - ein Knopf ohne eigenen Zweck.
  const ziele = istBuero
    ? [
        { id: 'start',      text: 'Heute',      pfad: 'M3 10.5 12 3l9 7.5 M5 9.5V21h14V9.5' },
        { id: 'kalender',   text: 'Kalender',   pfad: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4' },
        { id: 'protokolle', text: 'Protokolle', pfad: 'M6 3h9l4 4v14H6z M14 3v5h5' },
        { id: 'stationen',  text: 'Stationen',  pfad: 'M13 2 4 14h7l-1 8 9-12h-7z' },
        { id: 'mehr',       text: 'Mehr',       pfad: 'M5 12h.01 M12 12h.01 M19 12h.01' },
      ]
    : [
        { id: 'start',     text: 'Heute',     pfad: 'M3 10.5 12 3l9 7.5 M5 9.5V21h14V9.5' },
        { id: 'kalender',  text: 'Kalender',  pfad: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4' },
        { id: 'stationen', text: 'Stationen', pfad: 'M13 2 4 14h7l-1 8 9-12h-7z' },
        { id: 'kunden',    text: 'Kunden',    pfad: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21v-1a8 8 0 0 1 16 0v1' },
        { id: 'mehr',      text: 'Mehr',      pfad: 'M5 12h.01 M12 12h.01 M19 12h.01' },
      ];
  return (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.white,
                  borderTop: '1px solid ' + C.gray200, display: 'grid',
                  gridTemplateColumns: 'repeat(' + ziele.length + ', 1fr)', padding: '6px 4px 10px',
                  boxShadow: '0 -2px 14px rgba(10,22,40,0.10)', zIndex: 90 }}>
      {ziele.map((z, i) => {
        const istAktiv = aktiv === z.id;
        return (
          <button key={z.id + i} type="button" onClick={() => onWechsel(z.id)}
            aria-current={istAktiv ? 'page' : undefined}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                     display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                     padding: '8px 2px', minHeight: 52, borderRadius: 8,
                     color: istAktiv ? C.green : C.gray500, fontSize: 12, fontWeight: 700 }}>
            <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor"
                 strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d={z.pfad}/>
            </svg>
            {t(z.text)}
          </button>
        );
      })}
    </nav>
  );
}

// --- SERVICE WORKER REGISTRIERUNG --------------------------------------------
function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const reg = await navigator.serviceWorker.register('/service-worker.js');
        console.log('[ETB PWA] Service Worker registriert:', reg.scope);

        // Background Sync registrieren
        // Darf nie den Rest verhindern: Lehnt der Browser ab, wurde vorher
        // auch die Update-Pruefung darunter nie eingerichtet.
        if ('sync' in reg) {
          try { await reg.sync.register('sync-protokolle'); } catch { /* egal */ }
        }

        // Alle zwei Minuten nachsehen, ob eine neue Fassung bereitliegt.
        // Am Telefon gibt es kein Strg+Shift+R; ohne diese Pruefung kann eine
        // Aenderung tagelang unbemerkt bleiben.
        setInterval(() => { reg.update().catch(() => {}); }, 120000);
        reg.addEventListener('updatefound', () => {
          const neu = reg.installing;
          if (!neu) return;
          neu.addEventListener('statechange', () => {
            // Nur melden, wenn schon eine Fassung lief - sonst ist es die
            // Erstinstallation und es gibt nichts zu aktualisieren.
            if (neu.state === 'installed' && navigator.serviceWorker.controller) {
              // Merker zuerst setzen: Das Ereignis kommt nur einmal. Ist der
              // Balken gerade nicht eingehaengt, liest er beim Einhaengen den Merker.
              window.__etbNeueFassung = true;
              window.dispatchEvent(new CustomEvent('etb-neue-fassung'));
            }
          });
        });

        // Nachrichten vom SW empfangen (Background Sync Trigger)
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'BACKGROUND_SYNC') {
            abarbeiten();
          }
        });
      } catch (err) {
        console.warn('[ETB PWA] SW Registrierung fehlgeschlagen:', err);
      }
    });
  }
}

registerServiceWorker();
// Fehler, die keine Seite abfaengt, landen im Fehlerprotokoll (lib/fehlerprotokoll.js).
globaleFehlerFangen();


// --- HINWEIS AUF EINE NEUE FASSUNG -------------------------------------------
// Am Telefon gibt es kein Strg+Shift+R. Ohne diesen Balken laeuft dort im
// Zweifel wochenlang die alte Fassung weiter - und niemand merkt es.
//
// 13.09.2026: Der Balken stand nur auf der Startseite und hoerte auf ein
// einmaliges Ereignis. Kam die neue Fassung, waehrend ein Protokoll offen war,
// ging die Meldung verloren. Jetzt steht er ueber JEDER Seite und liest beim
// Einhaengen den Merker window.__etbNeueFassung.
function NeueFassungBanner() {
  const [da, setDa] = useState(() => !!window.__etbNeueFassung);
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => {
    const merken = () => setDa(true);
    if (window.__etbNeueFassung) setDa(true);
    window.addEventListener('etb-neue-fassung', merken);
    return () => window.removeEventListener('etb-neue-fassung', merken);
  }, []);

  // Vor dem Neuladen: offene Eingaben in die Warteschlange schieben und
  // moeglichst noch senden - aber hoechstens 4 s warten. Was dann noch nicht
  // draussen ist, liegt sicher auf dem Geraet und geht nach dem Neuladen raus.
  const jetztLaden = async () => {
    if (speichert) return;
    setSpeichert(true);
    try {
      window.dispatchEvent(new CustomEvent('etb-jetzt-speichern'));
      await new Promise(r => setTimeout(r, 300));
      await Promise.race([
        abarbeiten().catch(() => {}),
        new Promise(r => setTimeout(r, 4000)),
      ]);
    } catch {
      // Egal - neu geladen wird trotzdem.
    }
    window.location.reload();
  };

  if (!da) return null;
  return (
    <div style={{ background: C.green, color: C.white, padding: '10px 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 12, flexWrap: 'wrap', fontSize: 15, fontWeight: 700 }}>
      <span>{t('Eine neue Fassung der App steht bereit.')}</span>
      <button type="button" onClick={jetztLaden} disabled={speichert}
        style={{ padding: '8px 16px', borderRadius: 8, border: 'none',
                 background: C.white, color: C.green, fontWeight: 800,
                 fontSize: 15, cursor: speichert ? 'default' : 'pointer', fontFamily: 'inherit',
                 opacity: speichert ? 0.7 : 1 }}>
        {speichert ? t('speichert …') : t('Jetzt laden')}
      </button>
    </div>
  );
}

// --- SYNC STATUS BADGE -------------------------------------------------------
// 15.05.: Badge wird nur noch angezeigt, wenn etwas Besonderes ist (sync, error,
// offline, ausstehende Aenderungen). Im Normalfall (idle) bleibt der Header sauber.
// Meldet sich nur, wenn es etwas zu sagen gibt. "Alles in Ordnung" braucht
// keinen gruenen Balken - das ist der Normalfall und kostet nur Aufmerksamkeit.
function SyncBadge({ status, pendingCount, onSync }) {
  const configs = {
    syncing: { color: C.orange,  text: 'wird gesendet …' },
    error:   { color: C.red,     text: 'Senden fehlgeschlagen' },
    offline: { color: C.gray500, text: 'Offline' },
  };
  if (!pendingCount && !configs[status]) return null;
  const cfg = configs[status] || { color: C.orange, text: 'wartet auf Netz' };
  return (
    <button onClick={onSync} title={t('Jetzt senden')}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
        borderRadius: 20, background: 'transparent', border: '1px solid ' + C.gray200,
        cursor: 'pointer', fontFamily: 'inherit' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: C.gray700 }}>
        {t(cfg.text)}{pendingCount > 0 ? ' (' + pendingCount + ')' : ''}
      </span>
    </button>
  );
}

// --- OFFLINE BANNER ----------------------------------------------------------
function OfflineBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  if (online) return null;
  return (
    <div style={{ background: C.orange, color: C.white, padding: '8px 16px', textAlign: 'center', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      📵 {t('Offline – Änderungen werden gespeichert und beim nächsten Empfang synchronisiert')}
    </div>
  );
}

// --- INSTALL PROMPT ----------------------------------------------------------
function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem('pwa-dismissed') === '1'; } catch { return false; } });

  useEffect(() => {
    const beiAngebot = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', beiAngebot);
    return () => window.removeEventListener('beforeinstallprompt', beiAngebot);
  }, []);

  if (!prompt || dismissed) return null;

  return (
    <div style={{ background: C.navyMid, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 20 }}>📱</span>
        <div>
          <div style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>App installieren</div>
          <div style={{ color: C.gray300, fontSize: 13 }}>Auf dem Homescreen für schnellen Zugriff</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => { setDismissed(true); try { localStorage.setItem('pwa-dismissed', '1'); } catch { /* egal */ } }}
          style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.navyLight}`, background: 'none', color: C.gray300, cursor: 'pointer', fontSize: 14 }}>
          Später
        </button>
        <button onClick={async () => { prompt.prompt(); await prompt.userChoice; setPrompt(null); }}
          style={{ padding: '6px 14px', borderRadius: 7, background: C.yellow, color: C.navy, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>
          ＋ Installieren
        </button>
      </div>
    </div>
  );
}

// --- PASSWORT ÄNDERN SEITE ---------------------------------------------------
// onBack: Ohne Zurueck-Knopf war das eine Sackgasse - "Mehr" fuehrt Monteure
// hierher, und die Wisch-Geste zurueck schloss die ganze App.
function ChangePasswordPage({ onBack }) {
  const { updatePassword } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setMsg({ type: 'error', text: t('Passwörter stimmen nicht überein.') }); return; }
    if (pw.length < 8) { setMsg({ type: 'error', text: t('Passwort muss mindestens 8 Zeichen lang sein.') }); return; }
    setLoading(true);
    const result = await updatePassword(pw);
    setLoading(false);
    if (result.success) {
      setMsg({ type: 'success', text: t('Passwort erfolgreich geändert!') });
    } else {
      setMsg({ type: 'error', text: result.message });
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {onBack && (
          <button type="button" onClick={onBack}
            style={{ marginBottom: 12, minHeight: 44, padding: '9px 16px', borderRadius: 8, border: '1.5px solid ' + C.gray200,
                     background: C.white, color: C.navy, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
            &lt; {t('Zurück zur Startseite')}
          </button>
        )}
        {/* Sprache - unter "Mehr", wo die Monteure sie finden. */}
        <div style={{ background: C.white, borderRadius: 14, padding: '18px 22px', marginBottom: 12, boxShadow: '0 4px 24px rgba(10,22,40,0.08)' }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: C.navy, marginBottom: 8 }}>🌐 Sprache / Jezik</div>
          <SprachWahl gross/>
          <div style={{ fontSize: 13, color: C.gray500, marginTop: 8 }}>{t('Protokolle und PDF bleiben für das Büro und den Kunden auf Deutsch.')}</div>
        </div>
        {/* Schriftgroesse - gilt nur fuer dieses Geraet. */}
        <div style={{ background: C.white, borderRadius: 14, padding: '18px 22px', marginBottom: 12, boxShadow: '0 4px 24px rgba(10,22,40,0.08)' }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: C.navy, marginBottom: 8 }}>🔠 {t('Schriftgröße')}</div>
          <SchriftWahl/>
          <div style={{ fontSize: 13, color: C.gray500, marginTop: 8 }}>{t('Gilt nur auf diesem Gerät. Die ganze App wird größer – Schrift, Knöpfe und Abstände.')}</div>
        </div>
        <div style={{ background: C.white, borderRadius: 14, padding: 28, boxShadow: '0 4px 24px rgba(10,22,40,0.12)' }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: C.navy, marginBottom: 6 }}>🔑 {t('Neues Passwort setzen')}</div>
          <div style={{ fontSize: 15, color: C.gray500, marginBottom: 20 }}>{t('Bitte wähle ein sicheres Passwort (mind. 8 Zeichen).')}</div>
          {msg && (
            <div style={{ background: msg.type === 'success' ? C.greenBg : C.redBg, border: `1px solid ${msg.type === 'success' ? C.green : C.red}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 15, color: msg.type === 'success' ? C.green : C.red }}>
              {msg.text}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.gray500, marginBottom: 5, textTransform: 'uppercase' }}>{t('Neues Passwort')}</div>
              <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" required minLength={8}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.gray500, marginBottom: 5, textTransform: 'uppercase' }}>{t('Passwort bestätigen')}</div>
              <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="••••••••" required
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${C.gray200}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}/>
            </div>
            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '13px', borderRadius: 9, background: C.yellow, color: C.navy, border: 'none', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
              {loading ? t('Speichert...') : '✅ ' + t('Passwort speichern')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// --- HAUPT-NAVIGATION ---------------------------------------------------------
function AppShell() {
  const { user, profile, signOut, isAdmin, isBuero, loading } = useAuth();
  useSprache();   // bei Sprachwechsel die ganze App neu zeichnen
  const [page, setPage] = useState('dashboard'); // 'dashboard' | 'admin' | 'change-password' | 'stammdaten' | 'vnb' | 'protokolle'
  const [stammdatenInitialTab, setStammdatenInitialTab] = useState(null); // 'kunden' | 'stationen'
  const [offenesProtokoll, setOffenesProtokoll] = useState(null);
  // Welche geplante Wartung wird gerade geaendert? null = neue Planung.
  const [planungId, setPlanungId] = useState(null);
  const planungAendern = (id) => { setPlanungId(id); setPlanStation(null); setPage('planen'); };
  // Aus "Fällige Wartungen" heraus planen: Station ist schon gewaehlt.
  const [planStation, setPlanStation] = useState(null);
  const [planVorbelegung, setPlanVorbelegung] = useState(null);
  // Sync-Anzeige: haengt jetzt an der Warteschlange. Vorher wurden
  // setSyncStatus und setPendingCount NIE aufgerufen -- das Symbol stand
  // dauerhaft auf "Bereit", egal was war. Bei einer App, die offline
  // arbeiten soll, ist eine Anzeige, die immer dasselbe sagt, schlimmer
  // als gar keine.
  const [syncStatus, setSyncStatus] = useState('idle');
  const [pendingCount, setPendingCount] = useState(0);
  const isMobile = useIsMobile(900);

  // Die Anzeige spiegelt jetzt den echten Zustand:
  //   offline            -> kein Netz
  //   ausstehende Anzahl -> so viele Antworten warten auf Uebertragung
  //   synced             -> alles draussen (kurz sichtbar, dann still)
  useEffect(() => {
    starteAutomatik();
    starteFotoAutomatik();
    const aktualisieren = (n) => {
      setPendingCount(n);
      if (!navigator.onLine) setSyncStatus('offline');
      else if (n > 0) setSyncStatus('syncing');
      else setSyncStatus('synced');
    };
    aktualisieren(wartendeAnzahl());
    const abmelden = beiWarteschlangeAenderung(aktualisieren);
    const beiNetzwechsel = () => aktualisieren(wartendeAnzahl());
    window.addEventListener('online', beiNetzwechsel);
    window.addEventListener('offline', beiNetzwechsel);
    return () => {
      abmelden();
      window.removeEventListener('online', beiNetzwechsel);
      window.removeEventListener('offline', beiNetzwechsel);
    };
  }, []);

  // Passwort-Reset URL erkennen
  useEffect(() => {
    if (window.location.hash.includes('type=recovery')) {
      setPage('change-password');
    }
  }, []);

  // Die Seite wird erst ausgewaehlt und dann in die Fehlergrenze gesetzt.
  // Stuerzt eine Seite beim Zeichnen ab, bleibt nicht mehr die ganze App
  // weiss - es erscheint die ruhige Meldung aus FehlerGrenze.jsx.
  const seiteWaehlen = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.bg, fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>⚡</div>
            <div style={{ color: C.navy, fontWeight: 700 }}>{t('ETB lädt...')}</div>
          </div>
        </div>
      );
    }

    if (!user) return <LoginPage />;
    // Vom Admin deaktiviert (Migration 46 nimmt zusaetzlich die Rechte weg).
    if (profile?.active === false) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: C.bg }}>
          <div style={{ background: C.white, borderRadius: 12, padding: 24, maxWidth: 420, textAlign: 'center', boxShadow: '0 1px 8px rgba(10,22,40,0.1)' }}>
            <div style={{ fontWeight: 800, color: C.navy, fontSize: 18, marginBottom: 8 }}>{t('Zugang deaktiviert')}</div>
            <div style={{ color: C.gray700, fontSize: 15, marginBottom: 16 }}>{t('Dein Zugang wurde vom Admin deaktiviert. Bitte im Büro melden.')}</div>
            <button type="button" onClick={signOut} style={{ minHeight: 46, padding: '10px 20px', borderRadius: 8, border: 'none', background: C.navy, color: C.white, fontWeight: 800, cursor: 'pointer' }}>{t('Abmelden')}</button>
          </div>
        </div>
      );
    }
    // Ein Protokoll direkt von der Startseite geoeffnet
    if (offenesProtokoll) {
      return <ProtokollFormPage protokollId={offenesProtokoll} onBack={() => setOffenesProtokoll(null)} />;
    }
    if (page === 'planen' && (isAdmin || isBuero)) {
      const zurueck = planStation ? 'faellig' : 'dashboard';
      return <WartungPlanen protokollId={planungId} startStation={planungId ? null : planStation}
        vorbelegung={planungId || !planStation ? null : planVorbelegung}
        onAbbrechen={() => { setPlanungId(null); setPlanStation(null); setPlanVorbelegung(null); setPage(zurueck); }}
        onFertig={() => { setPlanungId(null); setPlanStation(null); setPlanVorbelegung(null); setPage(zurueck); }} />;
    }
    if (page === 'vertragsimport' && (isAdmin || isBuero)) {
      return <VertragsImport onBack={() => setPage('faellig')}/>;
    }
    if (page === 'faellig' && (isAdmin || isBuero)) {
      return <FaelligeWartungen onBack={() => setPage('dashboard')} onImport={() => setPage('vertragsimport')}
        onPlanen={(station, vorbelegung) => { setPlanungId(null); setPlanStation(station); setPlanVorbelegung(vorbelegung || null); setPage('planen'); }} />;
    }
    if (page === 'kalender') return (
      <Kalender
        userId={user.id}
        istBuero={isAdmin || isBuero}
        onOeffnenProtokoll={(id) => setOffenesProtokoll(id)}
        onZurueck={() => setPage('dashboard')}
        onPlanungAendern={(isAdmin || isBuero) ? planungAendern : undefined}
      />
    );
    if (page === 'change-password') return <ChangePasswordPage onBack={() => setPage('dashboard')} />;
    if (page === 'admin' && isAdmin) return <AdminPage onBack={() => setPage('dashboard')} onOpenVnb={() => setPage('vnb')} onOpenPasswort={() => setPage('change-password')} />;
    if (page === 'vnb' && (isAdmin || isBuero)) return <VnbVerwaltung onBack={() => setPage('admin')} />;
    if (page === 'stammdaten') return <StammdatenPage onBack={() => setPage('dashboard')} initialTab={stammdatenInitialTab} />;
    // Protokoll-Uebersicht: Buero-Arbeitsliste "welches Protokoll ist noch nicht
    // beim Kunden?". Nur fuer Buero und Admin, Monteure brauchen sie nicht.
    if (page === 'protokolle' && (isAdmin || isBuero)) return <ProtokollUebersicht onBack={() => setPage('dashboard')} />;

    // Haupt-App-Shell (Dashboard wird hier geladen)
    return (
      <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
        <OfflineBanner />
        <InstallBanner />

        {/* Kopfzeile - bewusst hell und ruhig. Das Logo hat einen weissen
            Hintergrund eingebrannt; auf hellem Grund faellt das nicht auf,
            auf dunklem sah es aus wie ein aufgeklebter Zettel. */}
        <div style={{ background: C.white, borderBottom: '1px solid ' + C.gray200, position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
              {/* Logo & Titel */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={LOGO_DATA_URL} alt="ETB" style={{ height: 32, objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }}/>
                <div style={{ borderLeft: '1px solid ' + C.gray200, paddingLeft: 12 }}>
                  <div style={{ color: C.navy, fontWeight: 700, fontSize: 15 }}>{t('Wartungsprotokoll')}</div>
                  <div style={{ color: C.gray500, fontSize: 12.5 }}>
                    {anzeigeName(profile, user)} · {t(ROLLE_TEXT[profile?.role] || 'Monteur')}
                    <span style={{ color: C.gray500 }}> · {APP_VERSION}</span>
                  </div>
                </div>
              </div>

              {/* Rechte Seite */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <SprachWahl/>
                <SyncBadge
                  status={navigator.onLine ? syncStatus : 'offline'}
                  pendingCount={pendingCount}
                  onSync={() => abarbeiten()}
                />
                {isMobile ? (
                  <MobileMenu
                    isAdmin={isAdmin}
                    zeigeProtokolle={isAdmin || isBuero}
                    onPlanen={() => { setPlanungId(null); setPlanStation(null); setPage('planen'); }}
                    onFaellig={() => setPage('faellig')}
                    onProtokolle={() => setPage('protokolle')}
                    onKunden={() => { setStammdatenInitialTab('kunden'); setPage('stammdaten'); }}
                    onStationen={() => { setStammdatenInitialTab('stationen'); setPage('stammdaten'); }}
                    onVnb={() => setPage('vnb')}
                    onAdmin={() => setPage('admin')}
                    onSignOut={signOut}
                  />
                ) : (
                  <>
                    {(isAdmin || isBuero) && (
                      <KopfLink onClick={() => { setPlanungId(null); setPlanStation(null); setPage('planen'); }}>{t('Wartung planen')}</KopfLink>
                    )}
                    {(isAdmin || isBuero) && (
                      <KopfLink onClick={() => setPage('faellig')}>{t('Fällige Wartungen')}</KopfLink>
                    )}
                    {(isAdmin || isBuero) && (
                      <KopfLink onClick={() => setPage('protokolle')}>{t('Protokolle')}</KopfLink>
                    )}
                    <KopfLink onClick={() => { setStammdatenInitialTab('kunden'); setPage('stammdaten'); }}>{t('Kunden')}</KopfLink>
                    <KopfLink onClick={() => { setStammdatenInitialTab('stationen'); setPage('stammdaten'); }}>{t('Stationen')}</KopfLink>
                    {(isAdmin || isBuero) && <KopfLink onClick={() => setPage('vnb')}>VNB</KopfLink>}
                    {isAdmin && <KopfLink onClick={() => setPage('admin')}>{t('Verwaltung')}</KopfLink>}
                    <KopfLink onClick={signOut} leise>{t('Abmelden')}</KopfLink>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Startseite: die anstehenden Wartungen statt eines leeren Begruessungs-
            kastens. Der Monteur sieht sofort, wo er hin muss. */}
        <div style={{ paddingBottom: 84 }}>
          <MonteurStart
            profile={profile}
            userId={user.id}
            onOeffnenProtokoll={(id) => setOffenesProtokoll(id)}
            // Buero/Admin planen mit Termin und Zuweisung, Monteure legen wie
            // bisher ueber die Stationsliste selbst eine Wartung an.
            onNeueWartung={() => {
              if (isAdmin || isBuero) { setPlanungId(null); setPlanStation(null); setPage('planen'); }
              else { setStammdatenInitialTab('stationen'); setPage('stammdaten'); }
            }}
            neueWartungText={(isAdmin || isBuero) ? t('+ Wartung planen') : t('+ Wartung selbst anlegen')}
            onPlanungAendern={(isAdmin || isBuero) ? planungAendern : undefined}
          />
        </div>

        {/* Untere Navigationsleiste. Oben rechts erreicht der Daumen einhaendig
            nicht -- unten schon. Groesster Bedienungsgewinn der App. */}
        <UntereLeiste
          aktiv="start"
          istBuero={isAdmin || isBuero}
          onWechsel={(ziel) => {
            if (ziel === 'start') return;
            if (ziel === 'protokolle') setPage('protokolle');
            else if (ziel === 'stationen') { setStammdatenInitialTab('stationen'); setPage('stammdaten'); }
            else if (ziel === 'kunden') { setStammdatenInitialTab('kunden'); setPage('stammdaten'); }
            else if (ziel === 'kalender') setPage('kalender');
            else if (ziel === 'mehr') setPage(isAdmin ? 'admin' : 'change-password');
          }}
        />
      </div>
    );
  };

  const zurStartseite = () => {
    setOffenesProtokoll(null);
    setPlanungId(null);
    setPage('dashboard');
  };

  return (
    <>
      {/* Hinweis auf eine neue Fassung - ueber JEDER Seite, nicht nur der Startseite. */}
      <NeueFassungBanner />
      <FehlerGrenze onZurueck={zurStartseite} resetKey={page + '|' + (offenesProtokoll || '')}>
        {seiteWaehlen()}
      </FehlerGrenze>
    </>
  );
}

// Aeussere Fehlergrenze als letzte Rettung: faengt, was in der AppShell selbst
// (Kopfzeile, Anmeldung) abstuerzt. "Zurueck" heisst hier: neu laden.
export default function App() {
  return (
    <FehlerGrenze onZurueck={() => window.location.reload()}>
      <AuthProvider>
        <AppShell/>
      </AuthProvider>
    </FehlerGrenze>
  );
}
